import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { MutateOptions } from 'react-query';

import { RequisitesStatusToggle } from 'components';
import { RequisitesStatus } from 'enums';
import { usePayinRequisitesUtils } from 'hooks';
import { PayoutRequisites, Requisites } from 'types';

import { RequisitesBlocked } from './RequisitesBlocked';

type Props = {
  requisites: Requisites | PayoutRequisites;
  canChangeStatus?: boolean;
  isPayout?: boolean;
  updateStatus: (
    id: string,
    status: RequisitesStatus,
    options: MutateOptions<any, any, any, any>,
  ) => void;
};

export const RequisitesStatusInfo: React.FC<Props> = ({
  requisites,
  canChangeStatus,
  isPayout,
  updateStatus,
}) => {
  const [status, setStatus] = useState(requisites.status);

  const { isVerified } = usePayinRequisitesUtils();

  const requisitesToggleDisabled = useMemo(
    () =>
      !canChangeStatus || (!isPayout && !isVerified(requisites as Requisites)),
    [canChangeStatus, isPayout, isVerified, requisites],
  );

  const handleStatusChange = useCallback(
    (updatedStatus: RequisitesStatus) =>
      updateStatus(requisites.id, updatedStatus, {
        onSuccess: () => setStatus(updatedStatus),
      }),
    [updateStatus, requisites.id],
  );

  useEffect(() => {
    setStatus(requisites.status);
  }, [requisites.status]);

  if (status === RequisitesStatus.Blocked) {
    return (
      <RequisitesBlocked
        blockedBy={requisites.blockedBy}
        blockedMessage={requisites.blockedMessage}
        blockedAt={requisites.blockedAt}
        statusDetails={requisites.statusDetails}
      />
    );
  }

  return (
    <Fragment>
      <RequisitesStatusToggle
        status={status}
        disabled={requisitesToggleDisabled}
        updateStatus={handleStatusChange}
      />
    </Fragment>
  );
};
